import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../shared/LoadingSpinner';
import { X } from 'lucide-react';

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { session, signIn } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('signup'); // 'login' | 'signup' — invitees usually don't have an account yet
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  // Set when the server accept endpoint already did the work, so the
  // session-change effect below doesn't re-run the RPC against a used token.
  const serverAcceptedRef = useRef(false);

  useEffect(() => {
    (async () => {
      // Fetch invitation details via the server endpoint (uses service_role)
      // rather than the anon Supabase client. This lets us turn on RLS for
      // public.invitations without breaking the accept flow — anon never
      // touches the table directly anymore. Server already filters by
      // status='pending' and returns 410 on expiry.
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
        const res = await fetch(`${API_URL}/invitations/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(res.status === 410
            ? 'This invitation has expired.'
            : (body.error || 'This invitation is invalid or has expired.'));
          setLoading(false);
          return;
        }
        const data = await res.json();
        setInvite(data);
        setForm(prev => ({ ...prev, email: data.email }));
        setLoading(false);
      } catch (e) {
        setError('This invitation is invalid or has expired.');
        setLoading(false);
      }
    })();
  }, [token]);

  const acceptInvitation = async () => {
    const { error: err } = await supabase.rpc('accept_invitation', {
      invitation_token: token,
    });
    if (err) {
      // Never show a Postgres message to an invitee.
      const m = err.message || '';
      setError(/expired/i.test(m) ? 'This invitation has expired — ask your team to send a new one.'
        : /not found|already used/i.test(m) ? 'This invitation was already used or revoked — ask your team to send a new one.'
        : /was sent to/i.test(m) ? `${m}. Sign out and open the link again from that mailbox.`
        : /duplicate key|violates|constraint/i.test(m) ? 'We could not add you to the team — please try the link again or ask your team to resend it.'
        : m || 'Could not accept the invitation. Please try again.');
      return false;
    }
    return true;
  };

  const sameEmail = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
  // The browser is signed in as someone other than the invitee — usually the
  // inviter opening their own link to check it. Accepting here would consume
  // the invitation for the wrong account (CITY SURGERY CENTER, 2026-11-02).
  const wrongAccount = Boolean(session && invite && !sameEmail(session.user?.email, invite.email));

  // If already logged in as the invitee, accept directly
  useEffect(() => {
    if (serverAcceptedRef.current) return;
    if (session && invite && !wrongAccount) {
      (async () => {
        setSubmitting(true);
        const ok = await acceptInvitation();
        if (ok) navigate('/app', { replace: true });
        setSubmitting(false);
      })();
    }
  }, [session, invite, wrongAccount]); // eslint-disable-line

  const switchAccount = async () => {
    // This browser only — the inviter's other devices stay signed in.
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (mode === 'signup') {
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters.');
        setSubmitting(false);
        return;
      }
      // Account creation goes through the server accept endpoint, which
      // creates a pre-confirmed user and returns a session. Client-side
      // supabase.auth.signUp() can't work here: with email confirmation
      // enabled it returns no session, so the accept RPC would run
      // unauthenticated and fail with "User not found".
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
        const res = await fetch(`${API_URL}/invitations/${token}/accept`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            password: form.password,
            first_name: form.firstName,
            last_name: form.lastName,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.session) {
          if (res.status === 404) {
            // The link was consumed after this page loaded — most often by
            // this same form a moment ago when the reply got lost. The
            // account exists; sign in with the password just chosen.
            setMode('login');
            setError('This invitation was already used. If you just created your account, sign in with your email and password.');
          } else {
            setError(body.error || 'Could not accept the invitation. Please try again.');
          }
          setSubmitting(false);
          return;
        }
        serverAcceptedRef.current = true;
        const { error: sessErr } = await supabase.auth.setSession({
          access_token: body.session.access_token,
          refresh_token: body.session.refresh_token,
        });
        if (sessErr) {
          setError(sessErr.message);
          setSubmitting(false);
          return;
        }
        navigate('/app', { replace: true });
        return;
      } catch {
        setError('Network error — please try again.');
        setSubmitting(false);
        return;
      }
    } else {
      const { error: err } = await signIn(form.email, form.password);
      if (err) {
        setError(err.message);
        setSubmitting(false);
        return;
      }
    }

    // After auth, accept the invitation
    const ok = await acceptInvitation();
    if (ok) navigate('/app', { replace: true });
    else setSubmitting(false);
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary p-4">
        <div className="w-full max-w-[420px] text-center">
          <div className="w-[48px] h-[48px] bg-danger-soft text-danger rounded-[14px] flex items-center justify-center mx-auto mb-4">
            <X size={24} strokeWidth={2.5} />
          </div>
          <h1 className="text-[22px] font-bold text-primary-c mb-2">Invalid Invitation</h1>
          <p className="text-[13px] text-muted-c mb-6">{error}</p>
          <Link to="/login" className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (wrongAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary p-4">
        <div className="w-full max-w-[420px]">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
            <h1 className="text-[22px] font-bold text-primary-c">You're invited</h1>
            <p className="text-[13px] text-muted-c mt-1">
              Join <strong className="text-primary-c">{invite.organization?.name}</strong> on MediLink
            </p>
          </div>
          <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg space-y-4">
            <p className="text-[13px] text-primary-c">
              This invitation was sent to <strong>{invite.email}</strong>, but this browser is signed in
              as <strong>{session.user?.email}</strong>.
            </p>
            <p className="text-[13px] text-muted-c">
              If you sent this invitation, nothing else is needed — the person it was sent to will open it from
              their own email. To accept it here anyway, sign out of this account first.
            </p>
            <button
              type="button"
              onClick={switchAccount}
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
            >
              Sign out and continue as {invite.email}
            </button>
            <Link to="/app" className="block text-center text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
              Back to MediLink
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (session && submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mb-4" />
          <p className="text-[13px] text-muted-c">Accepting invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[420px]">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
          <h1 className="text-[22px] font-bold text-primary-c">You're invited</h1>
          <p className="text-[13px] text-muted-c mt-1">
            Join <strong className="text-primary-c">{invite.organization?.name}</strong> on MediLink
          </p>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          {/* Toggle login/signup */}
          <div className="flex bg-tertiary rounded-[10px] p-1 mb-5">
            {['login', 'signup'].map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-[7px] text-[13px] font-medium transition-all ${
                  mode === m
                    ? 'bg-accent text-white shadow-glow'
                    : 'text-muted-c hover:text-primary-c'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            {mode === 'signup' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name</label>
                  <input type="text" className={inputClass} value={form.firstName} onChange={e => update('firstName', e.target.value)} required />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name</label>
                  <input type="text" className={inputClass} value={form.lastName} onChange={e => update('lastName', e.target.value)} required />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email</label>
              {/* The link is for one address; the server and the accept RPC
                  both hold the invitee to it, so don't let it be retyped. */}
              <input type="email" className={`${inputClass} opacity-70 cursor-not-allowed`} value={form.email} readOnly required />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Password</label>
              <input
                type="password"
                className={inputClass}
                placeholder={mode === 'signup' ? 'Minimum 8 characters' : 'Enter your password'}
                value={form.password}
                onChange={e => update('password', e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {submitting ? 'Processing...' : mode === 'login' ? 'Sign In & Accept' : 'Create Account & Accept'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
